import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { apiResetPasswordMobile } from "../net/requests";
import styles from "./verifyPage.module.scss";
import advanceYouLogo from "../assets/images/advanceYouLogo.png";
import TextInput from "../components/form/TextInput";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  getLanguageString,
  getCurrentLanguage,
  setLanguage,
} from "../misc/lang";

const ResetPasswordHrApp: React.FC = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const email = query.get("email") || "";
  const token = query.get("token") || "";

  const [emailState, setEmailState] = useState(email);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [lang, setLangState] = useState(getCurrentLanguage());
  const handleLanguageToggle = () => {
    const newLang = lang === "en" ? "sv" : "en";
    setLanguage(newLang);
    setLangState(newLang);
    window.location.reload();
  };

  const allRequiredFilled = emailState && newPassword && confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setPasswordError("");
    if (!allRequiredFilled) {
      setError(getLanguageString("verify", "all_fields_required"));
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError(getLanguageString("auth", "pw_error_length"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(getLanguageString("verify", "passwords_do_not_match"));
      return;
    }
    setLoading(true);
    try {
      await apiResetPasswordMobile({
        token,
        email: emailState,
        current_password: "", // Not required for reset
        new_password: newPassword,
      });
      setSuccess(true);
    } catch (err: any) {
      if (err?.response?.data?.error === "password-reset-token-mismatch") {
        setError(getLanguageString("verify", "invalid_or_expired_token"));
      } else {
        setError(
          err?.response?.data?.message ||
            getLanguageString("auth", "pw_reset_error"),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles["verify-page-container"]}>
      <div className={styles["verify-card"]}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 8,
          }}
        >
          <button
            onClick={handleLanguageToggle}
            style={{
              background: "#f7f7fb",
              border: "1px solid #e0e0e0",
              borderRadius: 16,
              padding: "4px 16px",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              color: "#222",
              outline: "none",
              minWidth: 80,
              transition: "background 0.2s",
            }}
            aria-label="Switch language"
          >
            {getCurrentLanguage() === "en" ? "🇸🇪 Svenska" : "🇬🇧 English"}
          </button>
        </div>
        <img
          src={advanceYouLogo}
          alt="AdvanceYou Logo"
          style={{
            display: "block",
            margin: "0 auto 18px",
            maxWidth: 120,
            borderRadius: "50%",
          }}
        />
        <h2 style={{ color: "#000", textAlign: "center" }}>
          {getLanguageString("auth", "reset_password_head")}
        </h2>
        {success ? (
          <div
            className={styles["success"]}
            style={{ margin: 24, textAlign: "center" }}
          >
            {getLanguageString("auth", "pw_reset_response")}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <TextInput
              label={getLanguageString("general", "email")}
              initialValue={emailState}
              updateValue={setEmailState}
            />
            <TextInput
              label={getLanguageString("verify", "new_password")}
              initialValue={newPassword}
              updateValue={setNewPassword}
              type={showNewPassword ? "text" : "password"}
              endIcon={showNewPassword ? <VisibilityOff /> : <Visibility />}
              iconAction={() => setShowNewPassword((v) => !v)}
              isRequired
            />
            <TextInput
              label={getLanguageString("verify", "confirm_password")}
              initialValue={confirmPassword}
              updateValue={setConfirmPassword}
              type={showConfirmPassword ? "text" : "password"}
              endIcon={showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              iconAction={() => setShowConfirmPassword((v) => !v)}
              isRequired
            />
            {passwordError && (
              <div className={styles["error"]}>{passwordError}</div>
            )}
            {error && <div className={styles["error"]}>{error}</div>}
            <div style={{ marginTop: 20 }}>
              <button
                type="submit"
                disabled={!allRequiredFilled || loading}
                style={{
                  width: "100%",
                  background: !(!allRequiredFilled || loading)
                    ? "#132829"
                    : "#bdbdbd",
                  color: "#fff",
                  fontWeight: 700,
                  border: "none",
                  borderRadius: 8,
                  padding: "12px 0",
                  fontSize: "1.1rem",
                  cursor: !(!allRequiredFilled || loading)
                    ? "pointer"
                    : "not-allowed",
                  opacity: !(!allRequiredFilled || loading) ? 1 : 0.6,
                  transition: "background 0.2s, opacity 0.2s",
                }}
              >
                {loading
                  ? getLanguageString("verify", "activating")
                  : getLanguageString("auth", "btn_trigger_password_reset")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordHrApp;
